import { ListItemIcon, ListItemText, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack, type Theme } from "@mui/material";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { differenceInMinutes, parseISO } from "date-fns";

import { TimeRangeLabel } from "../../../DateTime/TimeRangeLabel";
import { formatDayOfMonthWithDayName } from "../../../utils/formatDayOfMonthWithDayName";
import { formatHoursDurationCompact } from "../../../utils/formatHoursDurationCompact";
import { type TimeSlot } from "../../Open/types";
import { iconTypeByTimeSlotFilled } from "../../TimeSlotIcon";

interface ShiftInfoListItemContentProps {
  timeSlot: TimeSlot;
  startIso: string;
  endIso: string;
  payInMinorUnits: number | undefined;
  workplaceTimezone?: string;
  disabledReason?: string;
  iconColor?: (theme: Theme) => string;
}

export function ShiftInfoListItemContent(props: ShiftInfoListItemContentProps) {
  const {
    timeSlot,
    startIso,
    endIso,
    payInMinorUnits,
    workplaceTimezone,
    disabledReason,
    iconColor,
  } = props;

  const startDate = parseISO(startIso);
  const endDate = parseISO(endIso);
  const formattedDate = formatDayOfMonthWithDayName(startDate, { timezone: workplaceTimezone });
  const durationInHours = differenceInMinutes(endDate, startDate) / 60;
  const duration = formatHoursDurationCompact(durationInHours, { omitZeroMinutes: true });

  const primaryContent = isDefined(disabledReason) ? (
    <Text variant="body2">
      {disabledReason}
      {` \u00B7 `}
      {formattedDate}
    </Text>
  ) : (
    <Text variant="body2">
      {formattedDate}
      {` \u00B7 ${duration}`}
    </Text>
  );

  return (
    <>
      <ListItemIcon iconType={iconTypeByTimeSlotFilled[timeSlot]} color={iconColor} />
      <ListItemText
        disableTypography
        primary={
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {primaryContent}
            {isDefined(payInMinorUnits) && (
              <Text semibold variant="body2">
                {formatCentsAsUsd(payInMinorUnits)}
              </Text>
            )}
          </Stack>
        }
        secondary={
          <TimeRangeLabel
            semibold
            variant="body1"
            dateRange={{ startDate, endDate }}
            timezone={workplaceTimezone}
          />
        }
      />
    </>
  );
}
